import { useState } from "react";

import { EvaluationField } from "@/components/EvaluationField/EvaluationField";
import { Slider } from "@/components/Slider/Slider";
import { PlaygroundSectionLayout } from "../PlaygroundSectionLayout";
import { PlaygroundSubSectionLayout } from "../PlaygroundSubSectionLayout";
import { PlaygroundSubSectionTitle } from "../PlaygroundSubSectionTitle";

const ratingMarks = [
  { value: 0, label: "Very poor" },
  { value: 1, label: "Poor" },
  { value: 2, label: "Fair" },
  { value: 3, label: "Good" },
  { value: 4, label: "Excellent" },
];

type Rating = "VERY_POOR" | "POOR" | "FAIR" | "GOOD" | "EXCELLENT";

/**
 * Non-uniform answer points. maximumPoints is the highest answer's points
 * (here EXCELLENT = 10), exactly how EvaluationFormQuestion derives it via
 * `possibleAnswers.reduce(getHighestPoints, 0)`. The intermediate answers are
 * clustered low (0 / 1 / 2), so before CI-6015 Very poor/Poor/Fair bunched into
 * the left of the track (0 / 10 / 20%) and their labels collided, while Good sat
 * at 75% and Excellent at 100%. The slider now spaces marks by ordinal index, so
 * they stay evenly spread regardless of weighting. Selecting Good still scores
 * "7.5 / 10".
 */
const NON_UNIFORM_SCALE: Record<Rating, number> = {
  VERY_POOR: 0,
  POOR: 1,
  FAIR: 2,
  GOOD: 7.5,
  EXCELLENT: 10,
};

const MAXIMUM_POINTS = Math.max(...Object.values(NON_UNIFORM_SCALE));

function RatingQuestionCase() {
  const [rating, setRating] = useState<Rating>("GOOD");
  const [notApplicable, setNotApplicable] = useState(false);

  return (
    <div className="w-[560px]">
      <EvaluationField<Rating>
        mode="multiple"
        rank={1}
        question="¿Demostró el agente un sólido conocimiento del producto?"
        ratingScale={NON_UNIFORM_SCALE}
        maximumPoints={MAXIMUM_POINTS}
        rating={notApplicable ? ("NOT_APPLICABLE" as Rating) : rating}
        notApplicable={notApplicable}
        possiblyNotApplicable
        onChangeRating={(next) => next && setRating(next as Rating)}
        onChangeNotApplicable={setNotApplicable}
      />
    </div>
  );
}

export function PlaygroundSlider() {
  return (
    <PlaygroundSectionLayout>
      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Rating question — non-uniform points + NA toggle (CI-6015 fix)
        </PlaygroundSubSectionTitle>
        <RatingQuestionCase />
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>With marks</PlaygroundSubSectionTitle>
        <div className="flex w-[600px] flex-col gap-10">
          <Slider min={0} max={4} defaultValue={2} marks={ratingMarks} />
          <Slider min={0} max={4} defaultValue={0} marks={ratingMarks} />
          <Slider min={0} max={4} defaultValue={4} marks={ratingMarks} />
        </div>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>Without marks</PlaygroundSubSectionTitle>
        <div className="flex w-[600px] flex-col gap-10">
          <Slider min={0} max={100} defaultValue={50} />
        </div>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>Disabled</PlaygroundSubSectionTitle>
        <div className="flex w-[600px] flex-col gap-10">
          <Slider
            min={0}
            max={4}
            defaultValue={2}
            marks={ratingMarks}
            disabled
          />
        </div>
      </PlaygroundSubSectionLayout>
    </PlaygroundSectionLayout>
  );
}
